import { toast } from "react-toastify";
import AuthService from "../../AuthService";
import ReactGA from "react-ga4";

const API_URL = 'https://api.rb.beeapp.es';
const token = AuthService.getToken();

class CartService{

    //Get cart by user - MEJORADO: mejor manejo de errores
    static async getCart() {
        try {
            const response = await fetch(`${API_URL}/cart/items`, {
                method: 'GET',
                headers:{
                    'Authorization': `Bearer ${token}`,
                },
            });
            
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            
            const data = await response.json();
            
            // ✅ NUEVO: Validar estructura de respuesta
            if (!data || !Array.isArray(data.items)) {
                console.warn('Formato de carrito inesperado:', data);
                return { items: [], _id: null };
            }
            
            return data;
        }
        catch (error) {
            console.error('Error obteniendo carrito:', error);
            toast.error('Error al cargar el carrito');
            return { items: [], _id: null };
        }
    }

    //Add product to cart - MEJORADO: mejor validación
    static async addProductToCart(product) {
        const { id, quantity } = product;

        try {
            const response = await fetch(`${API_URL}/cart/`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    productID: id,
                    quantity,
                }),
            });

            const data = await response.json();

            if (!response.ok) {
                // ✅ MEJORADO: Mejor manejo de errores específicos de stock
                let errorMessage = data.message || 'No se pudo agregar el producto al carrito';
                
                // Manejo específico para errores de stock
                if (data.message && data.message.includes('stock')) {
                    if (data.availableStock !== undefined) {
                        errorMessage = `Stock insuficiente. Disponible: ${data.availableStock}, solicitado: ${data.requestedQuantity || quantity}`;
                        if (data.suggestedAction) {
                            errorMessage += `. ${data.suggestedAction}`;
                        }
                    }
                    
                    // ✅ NUEVO: Toast más informativo para errores de stock
                    toast.error(errorMessage, {
                        duration: 6000, // 6 segundos para errores de stock
                        style: {
                            backgroundColor: '#fff3cd',
                            borderColor: '#ffeaa7',
                            color: '#856404',
                        }
                    });
                } else {
                    toast.error(errorMessage);
                }
                
                return { success: false, message: errorMessage, stockError: data.availableStock !== undefined };
            }

            toast.success('Producto agregado al carrito');
            
            // ✅ NUEVO: Analytics mejorado
            ReactGA.event({
                action: 'add_to_cart',
                category: 'ecommerce',
                label: `Product ID: ${id}`,
                value: quantity
            });
            
            return { success: true, data };
        } catch (error) {
            console.error('Error añadiendo producto:', error);
            toast.error('Error de conexión');
            return { success: false, message: 'Error de conexión' };
        }
    }

    // ✅ NUEVO: Función optimizada para añadir múltiples productos
    static async addMultipleProductsToCart(products) {
        const results = [];
        let successCount = 0;
        let errorCount = 0;

        for (const product of products) {
            const result = await this.addProductToCart(product);
            results.push({
                productID: product.id,
                quantity: product.quantity,
                success: result.success,
                message: result.message
            });
            
            if (result.success) {
                successCount++;
            } else {
                errorCount++;
            }
            
            // Pausa pequeña para evitar sobrecarga del servidor
            await new Promise(resolve => setTimeout(resolve, 100));
        }

        if (successCount > 0) {
            toast.success(`${successCount} productos añadidos al carrito${errorCount > 0 ? `, ${errorCount} con errores` : ''}`);
        } else {
            toast.error('No se pudieron añadir productos al carrito');
        }

        return {
            success: successCount > 0,
            totalProcessed: products.length,
            successCount,
            errorCount,
            results
        };
    }

    // MEJORADO: Mejor manejo de errores
    static async deleteProductFromCart(id) {
        try {
            const response = await fetch(`${API_URL}/cart/items/${id}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                }
            });
            
            if (!response.ok) {
                const data = await response.json();
                throw new Error(data.message || 'Error al eliminar producto');
            }
            
            const data = await response.json();
            toast.success('Producto eliminado del carrito');
            
            return { success: true, data };
        } catch (error) {
            console.error('Error eliminando producto:', error);
            toast.error(error.message || 'Error al eliminar producto');
            return { success: false, message: error.message };
        }
    }

    // MEJORADO: Validación de cantidad y mejor manejo
    static async addProductQuantity(id, quantity) {
        try {
            // ✅ NUEVO: Validación previa
            if (!id || quantity < 0) {
                throw new Error('Datos inválidos');
            }
            
            const response = await fetch(`${API_URL}/cart/add`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    productID: id,
                    quantity: Number(quantity), // Asegurar que sea número
                }),
            });
            
            if (!response.ok) {
                const data = await response.json();
                
                // ✅ MEJORADO: Mostrar errores específicos de stock al cambiar cantidad
                if (data.message && data.message.includes('stock')) {
                    let errorMessage = data.message;
                    if (data.availableStock !== undefined) {
                        errorMessage = `Stock insuficiente. Disponible: ${data.availableStock}, solicitado: ${data.requestedQuantity || quantity}`;
                        if (data.suggestedAction) {
                            errorMessage += `. ${data.suggestedAction}`;
                        }
                    }
                    
                    toast.error(errorMessage, {
                        duration: 5000,
                        style: {
                            backgroundColor: '#fff3cd',
                            borderColor: '#ffeaa7',
                            color: '#856404',
                        }
                    });
                }
                
                throw new Error(data.message || 'Error al actualizar cantidad');
            }
            
            const data = await response.json();
            return { success: true, data };
        } catch (error) {
            console.error('Error actualizando cantidad:', error);
            return { success: false, message: error.message };
        }
    }

    // OPTIMIZADO: Mejor manejo
    static async getTotalItems() {
        try {
            const response = await fetch(`${API_URL}/cart/total`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                }
            });
            
            if (!response.ok) {
                throw new Error('Error obteniendo total de items');
            }
            
            const data = await response.json();
            return Number(data) || 0;
        } catch (error) {
            console.error('Error obteniendo total de items:', error);
            return 0;
        }
    }

    // ✅ NUEVO: Función para validar disponibilidad de stock antes de checkout
    static async validateCartStock() {
        try {
            const cart = await this.getCart();
            const invalidItems = [];
            
            for (const item of cart.items || []) {
                if (!item.stock) {
                    invalidItems.push({
                        id: item._id,
                        name: item.name,
                        requestedQuantity: item.quantity
                    });
                }
            }
            
            return {
                isValid: invalidItems.length === 0,
                invalidItems
            };
        } catch (error) {
            console.error('Error validando stock:', error);
            return { isValid: false, invalidItems: [] };
        }
    }

    // ✅ NUEVO: Función para mostrar advertencias de stock visualmente
    static showStockWarning(item) {
        if (!item.stockInfo || !item.stockInfo.warnings) return;
        
        const warnings = item.stockInfo.warnings.filter(w => w.severity === 'error');
        if (warnings.length > 0) {
            const warning = warnings[0];
            toast.warning(`${item.name}: ${warning.message}`, {
                duration: 4000,
                style: {
                    backgroundColor: '#fff3cd',
                    borderColor: '#ffeaa7',
                    color: '#856404',
                }
            });
        }
    }

    // ✅ NUEVO: Función para validar todo el carrito y mostrar errores
    static async validateAndShowCartErrors(showToasts = true) {
        try {
            const cart = await this.getCart();
            let hasStockIssues = false;
            
            for (const item of cart.items || []) {
                if (item.stockInfo && item.stockInfo.warnings) {
                    const criticalWarnings = item.stockInfo.warnings.filter(w => w.severity === 'error');
                    if (criticalWarnings.length > 0) {
                        hasStockIssues = true;
                        if (showToasts) {
                            this.showStockWarning(item);
                        }
                    }
                }
            }
            
            return { hasStockIssues, cart };
        } catch (error) {
            console.error('Error validando carrito:', error);
            return { hasStockIssues: false, cart: null };
        }
    }

    // ✅ NUEVO: Función para calcular totales con descuentos (unificada)
    static calculateCartTotals(cartItems) {
        let subtotal = 0;
        let totalWithDiscounts = 0;
        let totalSavings = 0;

        for (const item of cartItems) {
            const quantity = Number(item.quantity) || 0;
            const originalPrice = Number(item.price) || 0;
            let finalPrice = originalPrice;

            // Aplicar descuentos si existen
            if (item.discounts && item.discounts.length > 0) {
                const applicableDiscount = item.discounts
                    .filter(discount => quantity >= discount.minQuantity)
                    .sort((a, b) => b.minQuantity - a.minQuantity)[0]; // Tomar el mejor descuento

                if (applicableDiscount) {
                    finalPrice = applicableDiscount.discountedPrice;
                }
            }

            const itemSubtotal = originalPrice * quantity;
            const itemTotal = finalPrice * quantity;
            
            subtotal += itemSubtotal;
            totalWithDiscounts += itemTotal;
            totalSavings += (itemSubtotal - itemTotal);
        }

        return {
            subtotal: Number(subtotal.toFixed(2)),
            total: Number(totalWithDiscounts.toFixed(2)),
            savings: Number(totalSavings.toFixed(2)),
            shipping: totalWithDiscounts >= 100 ? 0 : 6,
            finalTotal: Number((totalWithDiscounts + (totalWithDiscounts >= 100 ? 0 : 6)).toFixed(2))
        };
    }
}

export default CartService;